/**
 * Люди всех компаний (ЛЦ-10): поиск по имени и почте, отбор, карточка человека с компаниями и ролями, сеансами,
 * входами и блокировками; заблокировать и разблокировать вход, завершить сеансы, снять блокировку перебора,
 * отправить ссылку для нового пароля, передать владение компанией (только владелец портала).
 */
import {
  CONSOLE_USER_FILTER_TITLES,
  CONSOLE_USER_FILTERS,
  type ConsoleUserDetail,
  type ConsoleUserFilter,
  type ConsoleUserList,
  LOGIN_LOCK_KIND_TITLES,
  LOGIN_METHOD_TITLES,
  LOGIN_RESULT_TITLES,
  type LoginEvent,
  type MemberPasswordResetResult,
  MONEY_LEVELS,
  PASSWORD_RESET_ADMIN_TTL_HOURS,
} from '@epc/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import { KeyRound, MailCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { dateTime, relative } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Textarea } from '../../ui/form';
import { KeyValue, Panel } from '../../ui/layout';
import { EntriesList } from './CompanyConsole';
import { roleTitle } from './console-shared';

const PAGE = 50;

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function UsersPage() {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<ConsoleUserFilter>('all');
  const [limit, setLimit] = useState(PAGE);
  const query = useDebounced(q.trim());
  const list = useQuery({
    queryKey: ['platform', 'users', query, filter, limit],
    queryFn: () =>
      api<ConsoleUserList>('/platform/users', { query: { q: query || undefined, filter, limit } }),
    placeholderData: keepPreviousData,
  });
  return (
    <Panel
      title="Люди всех компаний"
      meta={list.data ? `${list.data.total}` : undefined}
      actions={
        <Input
          type="search"
          aria-label="Имя или почта"
          placeholder="Имя или почта"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setLimit(PAGE);
          }}
          className="w-full sm:w-64"
        />
      }
      bodyClassName="p-0"
    >
      <fieldset
        className="flex min-w-0 gap-1 overflow-x-auto border-b border-line px-3 py-2"
        aria-label="Отбор"
      >
        {CONSOLE_USER_FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            aria-pressed={filter === f}
            onClick={() => {
              setFilter(f);
              setLimit(PAGE);
            }}
            className={clsx(
              'h-11 shrink-0 rounded-md px-3 text-[13px] whitespace-nowrap md:h-8',
              filter === f ? 'bg-ink font-medium text-on-ink' : 'text-ink-2 hover:bg-tile hover:text-ink',
            )}
          >
            {CONSOLE_USER_FILTER_TITLES[f]}
          </button>
        ))}
      </fieldset>
      {list.isPending ? (
        <div className="px-5">
          <Spinner />
        </div>
      ) : list.isError ? (
        <div className="p-5">
          <ErrorBox error={list.error} />
        </div>
      ) : !list.data.items.length ? (
        <div className="p-5">
          <EmptyState title="Никого не нашлось">
            Проверьте имя или почту, или выберите другой отбор.
          </EmptyState>
        </div>
      ) : (
        <>
          <ul className="divide-y divide-line">
            {list.data.items.map((u) => (
              <li key={u.id}>
                <Link
                  to="/platform/users/$id"
                  params={{ id: u.id }}
                  className="flex min-h-11 flex-col gap-0.5 px-5 py-2.5 hover:bg-surface-2"
                >
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-ink [overflow-wrap:anywhere]">{u.fullName}</span>
                    {u.platformRole ? (
                      <Badge tone="ink">{roleTitle(u.platformRole).toLowerCase()}</Badge>
                    ) : null}
                    {u.blocked ? <Badge tone="danger">вход заблокирован</Badge> : null}
                    {u.locked ? <Badge tone="warning">перебор пароля</Badge> : null}
                  </span>
                  <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                    {u.email} · последний вход {relative(u.lastLoginAt)}
                  </span>
                  <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                    {u.companies.length
                      ? u.companies
                          .map(
                            (c) =>
                              `${c.tenantName}${c.isOwner ? ' (владелец)' : c.kind === 'external' ? ' (внешний)' : ''}${c.status !== 'active' ? ' — отключён' : ''}`,
                          )
                          .join(' · ')
                      : 'ни в одной компании'}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          {list.data.total > list.data.items.length ? (
            <div className="border-t border-line px-5 py-3">
              <Button size="sm" loading={list.isFetching} onClick={() => setLimit((l) => l + PAGE)}>
                Показать ещё
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Panel>
  );
}

const KIND_TITLES: Record<string, string> = { staff: 'сотрудник', external: 'внешний участник' };

export function LoginList({ events, showWho }: { events: LoginEvent[]; showWho?: boolean }) {
  if (!events.length) return <p className="px-5 py-4 text-[14px] text-ink-2">Входов пока нет.</p>;
  return (
    <ul className="divide-y divide-line">
      {events.map((e) => (
        <li key={e.id} className="flex flex-col gap-0.5 px-5 py-2.5 text-[14px]">
          <span className="flex flex-wrap items-center gap-2">
            <span className="num text-[12px] text-ink-2">{dateTime(e.at)}</span>
            <Badge tone={e.result === 'ok' ? 'success' : e.result === 'locked' ? 'danger' : 'warning'}>
              {LOGIN_RESULT_TITLES[e.result]}
            </Badge>
            {e.newDevice ? <Badge tone="accent">новое устройство</Badge> : null}
            {showWho ? (
              <span className="font-medium text-ink [overflow-wrap:anywhere]">
                {e.userName ? `${e.userName} · ` : ''}
                {e.email}
              </span>
            ) : null}
          </span>
          <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
            {LOGIN_METHOD_TITLES[e.method]}
            {e.tenantName ? ` · ${e.tenantName}` : ''}
            {e.ip ? ` · ${e.ip}` : ''}
            {e.device ? ` · ${e.device}` : ''}
          </span>
        </li>
      ))}
    </ul>
  );
}

function BlockDialog({ user, onClose }: { user: ConsoleUserDetail['user']; onClose: () => void }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const block = useMutation({
    mutationFn: () => api(`/platform/users/${user.id}/block`, { body: { reason } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform'] });
      toast('Вход заблокирован, сеансы завершены');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      width="sm"
      title={`Заблокировать вход: ${user.fullName}?`}
      description="Человек сразу выйдет на всех устройствах и не сможет войти ни в одну компанию, пока вход не разблокируют."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="danger"
            loading={block.isPending}
            disabled={reason.trim().length < 3}
            onClick={() => block.mutate()}
          >
            Заблокировать
          </Button>
        </>
      }
    >
      <Field
        label="Причина"
        htmlFor="block-reason"
        hint="Видна команде портала в карточке человека и журнале"
      >
        <Textarea
          id="block-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          placeholder="Например: уволен, доступ закрыть"
        />
      </Field>
      <ErrorBox className="mt-3" error={block.error} />
    </Dialog>
  );
}

function OwnerDialog({
  user,
  company,
  onClose,
}: {
  user: ConsoleUserDetail['user'];
  company: { tenantId: string; tenantName: string };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [keep, setKeep] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api(`/platform/companies/${company.tenantId}/owner`, { body: { userId: user.id, keepPrevious: keep } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform'] });
      toast(`${user.fullName} — владелец компании «${company.tenantName}»`);
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      width="sm"
      title="Передать владение компанией?"
      description={`${user.fullName} станет владельцем «${company.tenantName}»: все права и все суммы компании.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            Передать
          </Button>
        </>
      }
    >
      <Checkbox
        label="Оставить прежнего владельца владельцем"
        description="Без отметки прежний владелец останется руководителем и администратором компании, но не владельцем"
        checked={keep}
        onChange={(e) => setKeep(e.target.checked)}
      />
      <p className="mt-3 text-[13px] text-ink-2">
        Изменение попадёт в историю компании и в журнал её лицензии под вашим именем.
      </p>
      <ErrorBox className="mt-3" error={save.error} />
    </Dialog>
  );
}

const RESET_TTL = PASSWORD_RESET_ADMIN_TTL_HOURS === 24 ? 'сутки' : `${PASSWORD_RESET_ADMIN_TTL_HOURS} ч.`;

/**
 * Ссылка для нового пароля (ЛЦ-10): то же письмо, что «Сбросить пароль» в «Сотрудниках», но от команды портала.
 * Ссылка уходит только на почту человека, отправивший её не видит; прежний пароль работает, пока не задан новый.
 */
function PasswordResetPanel({ userId, email }: { userId: string; email: string }) {
  const qc = useQueryClient();
  const [asking, setAsking] = useState(false);
  const send = useMutation({
    mutationFn: () =>
      api<MemberPasswordResetResult>(`/platform/users/${userId}/password-reset`, { method: 'POST' }),
    onSuccess: () => {
      setAsking(false);
      void qc.invalidateQueries({ queryKey: ['platform', 'users', 'card', userId] });
    },
  });
  const done = send.data;
  return (
    <Panel title="Пароль">
      {done ? (
        <p className="flex gap-2 rounded-md bg-success-soft px-3 py-2.5 text-[14px] text-ink" role="status">
          <MailCheck className="mt-0.5 size-5 shrink-0 text-success" aria-hidden />
          <span className="[overflow-wrap:anywhere]">
            Ссылка для нового пароля отправлена на {done.email}. Действует до {dateTime(done.expiresAt)} и
            срабатывает один раз.
          </span>
        </p>
      ) : asking ? (
        <div className="flex flex-col gap-2">
          <p className="text-[14px] text-ink [overflow-wrap:anywhere]">
            Отправить на {email} ссылку для нового пароля? В письме будет сказано, что её отправила команда
            портала. Прежний пароль работает, пока человек не задаст новый.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" loading={send.isPending} onClick={() => send.mutate()}>
              Отправить ссылку
            </Button>
            <Button variant="ghost" onClick={() => setAsking(false)}>
              Не отправлять
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-start gap-2">
          <p className="text-[13px] text-ink-2">
            Не может войти или забыл пароль — портал пришлёт ему письмо со ссылкой. Ссылка действует{' '}
            {RESET_TTL}, вы её не увидите.
          </p>
          <Button icon={<KeyRound className="size-4" />} onClick={() => setAsking(true)}>
            Отправить ссылку для нового пароля
          </Button>
        </div>
      )}
      <ErrorBox className="mt-3" error={send.error} />
    </Panel>
  );
}

export function UserCardPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['platform', 'users', 'card', id],
    queryFn: () => api<ConsoleUserDetail>(`/platform/users/${id}`),
  });
  const [dialog, setDialog] = useState<'block' | { tenantId: string; tenantName: string } | null>(null);
  useCrumbs(
    q.data ? [{ label: 'Люди', to: '/platform/users' }, { label: q.data.user.fullName }] : null,
    q.error,
  );
  const refresh = () => void qc.invalidateQueries({ queryKey: ['platform'] });
  const unblock = useMutation({
    mutationFn: () => api(`/platform/users/${id}/unblock`, { method: 'POST' }),
    onSuccess: () => {
      refresh();
      toast('Вход разблокирован');
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Не удалось', 'danger'),
  });
  const revoke = useMutation({
    mutationFn: () => api<{ ended: number }>(`/platform/users/${id}/sessions/revoke`, { method: 'POST' }),
    onSuccess: (r) => {
      refresh();
      toast(`Сеансов завершено: ${r.ended}`);
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Не удалось', 'danger'),
  });
  const unlock = useMutation({
    mutationFn: () => api<{ released: number }>(`/platform/users/${id}/unlock`, { method: 'POST' }),
    onSuccess: (r) => {
      refresh();
      toast(r.released ? 'Блокировка перебора снята — можно входить' : 'Действующих блокировок нет');
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Не удалось', 'danger'),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const d = q.data;
  const u = d.user;
  const activeLocks = d.locks.filter((l) => l.active);
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Link
            to="/platform/users"
            className="inline-flex h-11 items-center text-[14px] text-ink-2 underline-offset-4 hover:underline md:h-8"
          >
            ← Все люди
          </Link>
          <h2 className="text-[20px] leading-7 font-semibold text-ink [overflow-wrap:anywhere]">
            {u.fullName}
          </h2>
          {u.platformRole ? <Badge tone="ink">{roleTitle(u.platformRole).toLowerCase()}</Badge> : null}
          {d.blocked ? <Badge tone="danger">вход заблокирован</Badge> : null}
          {activeLocks.length ? <Badge tone="warning">перебор пароля</Badge> : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {activeLocks.length ? (
            <Button size="sm" loading={unlock.isPending} onClick={() => unlock.mutate()}>
              Снять блокировку перебора
            </Button>
          ) : null}
          {d.can.revokeSessions && d.sessions.length ? (
            <Button size="sm" loading={revoke.isPending} onClick={() => revoke.mutate()}>
              Завершить сеансы
            </Button>
          ) : null}
          {d.blocked ? (
            <Button size="sm" variant="primary" loading={unblock.isPending} onClick={() => unblock.mutate()}>
              Разблокировать вход
            </Button>
          ) : d.can.block ? (
            <Button size="sm" variant="danger" onClick={() => setDialog('block')}>
              Заблокировать вход
            </Button>
          ) : null}
        </div>
      </div>
      {d.blocked ? (
        <div className="rounded-md border border-danger/40 bg-danger-soft px-4 py-3 text-[14px] text-ink">
          Вход заблокирован{d.blocked.at ? ` ${dateTime(d.blocked.at)}` : ''}
          {d.blocked.byName ? `, ${d.blocked.byName}` : ''}
          {d.blocked.reason ? `: ${d.blocked.reason}` : ''}.
        </div>
      ) : null}
      <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="flex flex-col gap-5">
          <Panel title="Компании и роли" meta={`${d.companies.length}`} bodyClassName="p-0">
            {!d.companies.length ? (
              <p className="px-5 py-4 text-[14px] text-ink-2">Человек ни в одной компании.</p>
            ) : (
              <ul className="divide-y divide-line">
                {d.companies.map((c) => (
                  <li key={c.tenantId} className="flex flex-col gap-1 px-5 py-3 text-[14px]">
                    <span className="flex flex-wrap items-center gap-2">
                      <Link
                        to="/platform/tenants/$id"
                        params={{ id: c.tenantId }}
                        className="inline-flex min-h-11 items-center font-medium text-ink underline-offset-2 hover:underline md:min-h-0"
                      >
                        {c.tenantName}
                      </Link>
                      {c.isDemo ? <Badge tone="info">демо</Badge> : null}
                      {c.isOwner ? <Badge tone="ink">владелец</Badge> : null}
                      {c.kind !== 'staff' ? <Badge>{KIND_TITLES[c.kind] ?? c.kind}</Badge> : null}
                      {c.status !== 'active' ? <Badge>отключён в компании</Badge> : null}
                    </span>
                    <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                      {c.position ? `${c.position} · ` : ''}деньги:{' '}
                      {(MONEY_LEVELS as Record<string, string>)[c.moneyLevel] ?? c.moneyLevel} · с{' '}
                      {dateTime(c.joinedAt)}
                    </span>
                    {c.roles.length ? (
                      <span className="text-[13px] text-ink [overflow-wrap:anywhere]">
                        {c.roles.map((r) => `${r.name} (${r.scope})`).join(', ')}
                      </span>
                    ) : c.isOwner ? null : (
                      <span className="text-[13px] text-ink-2">Ролей нет</span>
                    )}
                    {d.can.makeOwner &&
                    !c.isOwner &&
                    c.kind === 'staff' &&
                    c.status === 'active' &&
                    u.isActive ? (
                      <div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDialog({ tenantId: c.tenantId, tenantName: c.tenantName })}
                        >
                          Сделать владельцем компании
                        </Button>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          <Panel title="Последние входы" meta="90 дней" bodyClassName="p-0">
            <LoginList events={d.logins} />
          </Panel>
          {u.platformRole ? (
            <Panel title="Входы в компании" bodyClassName="p-0">
              <EntriesList entries={d.entries} showCompany />
            </Panel>
          ) : null}
        </div>
        <div className="flex flex-col gap-5">
          <Panel title="Учётная запись">
            <KeyValue
              items={[
                { label: 'Почта', value: u.email },
                { label: 'Должность', value: u.position ?? '—' },
                { label: 'Зарегистрирован', value: dateTime(u.createdAt) },
                { label: 'Последний вход', value: u.lastLoginAt ? dateTime(u.lastLoginAt) : 'не входил' },
                { label: 'Face ID, Touch ID', value: u.hasPasskey ? 'включены' : 'нет' },
              ]}
            />
          </Panel>
          {d.can.passwordReset ? <PasswordResetPanel userId={u.id} email={u.email} /> : null}
          <Panel title="Открытые сеансы" meta={`${d.sessions.length}`} bodyClassName="p-0">
            {!d.sessions.length ? (
              <p className="px-5 py-4 text-[14px] text-ink-2">Открытых сеансов нет.</p>
            ) : (
              <ul className="divide-y divide-line">
                {d.sessions.map((s) => (
                  <li key={s.id} className="flex flex-col gap-0.5 px-5 py-2.5 text-[14px]">
                    <span className="text-ink">{s.device ?? 'устройство'}</span>
                    <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                      {s.ip ?? 'адрес не записан'} · в портале {relative(s.lastSeenAt)}
                      {s.tenantName ? ` · ${s.tenantName}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          {d.locks.length ? (
            <Panel title="Блокировки перебора" meta="30 дней" bodyClassName="p-0">
              <ul className="divide-y divide-line">
                {d.locks.map((l) => (
                  <li key={l.id} className="flex flex-col gap-0.5 px-5 py-2.5 text-[14px]">
                    <span className="text-ink">
                      {dateTime(l.lockedAt)} · {l.failures} неудач · до {dateTime(l.lockedUntil)}
                    </span>
                    <span className="text-[13px] text-ink-2">
                      {LOGIN_LOCK_KIND_TITLES[l.kind]} ·{' '}
                      {l.active
                        ? 'действует'
                        : l.releasedAt
                          ? `снята${l.releasedByName ? `: ${l.releasedByName}` : ''}${l.releaseNote ? ` (${l.releaseNote})` : ''}`
                          : 'закончилась'}
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>
          ) : null}
          {d.events.length ? (
            <Panel title="Журнал по человеку" bodyClassName="p-0">
              <ul className="divide-y divide-line">
                {d.events.map((e) => (
                  <li key={e.id} className="flex flex-col gap-0.5 px-5 py-2.5 text-[14px]">
                    <span className="text-ink">{e.title}</span>
                    <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                      {dateTime(e.createdAt)}
                      {e.actorName ? ` · ${e.actorName}` : ''}
                      {typeof e.details.reason === 'string' ? ` · ${e.details.reason}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>
          ) : null}
        </div>
      </div>
      {dialog === 'block' ? <BlockDialog user={u} onClose={() => setDialog(null)} /> : null}
      {dialog && dialog !== 'block' ? (
        <OwnerDialog user={u} company={dialog} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}
